/**
 * Environment-aware Supabase runtime client.
 *
 * App imports of `@/integrations/supabase/client` are aliased to this
 * file so the generated `./client.ts` can stay untouched for the
 * Supabase/Lovable connection.
 *
 * We intentionally fail closed when the env vars are missing instead of
 * silently falling back to a production project. That keeps staging and
 * preview builds from accidentally talking to production.
 *
 * See docs/deployment/SECRETS.md.
 */
import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "./types";

function readRequiredEnv(name: "VITE_SUPABASE_URL" | "VITE_SUPABASE_PUBLISHABLE_KEY"): string {
  const value = (import.meta.env[name] as string | undefined)?.trim();
  if (!value) {
    throw new Error(`[supabase] Missing required environment variable: ${name}`);
  }
  return value;
}

export const configuredSupabaseUrl = readRequiredEnv("VITE_SUPABASE_URL").replace(/\/+$/, "");
export const configuredSupabasePublishableKey = readRequiredEnv("VITE_SUPABASE_PUBLISHABLE_KEY");

export const supabase: SupabaseClient<Database> = createClient<Database>(
  configuredSupabaseUrl,
  configuredSupabasePublishableKey,
  {
    auth: {
      storage: localStorage,
      persistSession: true,
      autoRefreshToken: true,
    },
    realtime: { params: { eventsPerSecond: 2 } },
    global: { headers: { "x-client-info": "ticket-swiftly" } },
  },
);

supabase.realtime.disconnect();
